import { useAppAuth } from '@/lib/useAppAuth';
import { useKycStatus } from './useKyc';
import type { KycVerification } from './types';

/** Stable identity flow key — mirrors the api's IDENTITY_FLOW_KEY (frozen FE contract). */
export const IDENTITY_FLOW_KEY = 'identity-basic';

/**
 * The identity verification the user still has to pass, or null when satisfied.
 *
 * Mirrors the server's KycGuard rule (`isIdentitySatisfied`): identity is
 * satisfied only by an APPROVED verification, so a user who never started —
 * `/kyc/status` returns an empty `verifications` list until a Sumsub applicant
 * exists — is pending too; for that case a synthetic NOT_STARTED entry under
 * the stable flow key is returned. Null while signed out or while the status
 * query is loading/errored (fail open; the 403 KYC_REQUIRED backstop covers a
 * stale cache). Reads the shared `['kyc', 'status']` query, so gating on it
 * costs no extra request and flips reactively when the gate approves.
 */
export function useKycIdentityPending(): KycVerification | null {
  const { isAuthenticated } = useAppAuth();
  const { data } = useKycStatus(isAuthenticated);
  if (!data) return null;
  const identity = data.verifications?.filter((v) => v.enforcement === 'HARD') ?? [];
  if (identity.some((v) => v.status === 'APPROVED')) return null;
  return (
    identity[0] ?? {
      flowKey: IDENTITY_FLOW_KEY,
      displayName: 'Identity verification',
      enforcement: 'HARD',
      status: 'NOT_STARTED',
      reviewRejectType: null,
    }
  );
}
